import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  getDashboardCharts,
  getNetworkOverview,
  getPlatformOverview,
  getRecentCars,
} from "@/lib/analytics";
import {
  createCarOptionSet,
  createGasStationBrandWithLogo,
  createGasStationWithLogo,
  updateGasStationBrandWithLogo,
  deleteCar,
  deleteGasStation,
  deleteGasStationBrand,
  approveConnectedService,
  fetchBusinessById,
  fetchBusinesses,
  fetchCarsByCarIds,
  fetchCarById,
  fetchCarMakeLogos,
  fetchCarOptionSetById,
  fetchCarOptionSets,
  fetchCarOptionSetSummary,
  fetchCars,
  fetchCarsForAnalytics,
  fetchCarsByUserId,
  fetchConnectedServiceByBusinessId,
  fetchConnectedServices,
  fetchCustomers,
  fetchCustomersByBusiness,
  fetchFuelLogs,
  fetchFuelLogsByUserId,
  fetchGasStationById,
  fetchGasStationBrandById,
  fetchGasStationBrands,
  fetchGasStations,
  fetchServiceHistory,
  fetchServiceHistoryForCars,
  fetchServicePricesByBusiness,
  fetchShftServices,
  fetchShftServicesForCars,
  fetchUserById,
  fetchUserByUserId,
  fetchUserCount,
  fetchUsers,
  rejectConnectedService,
  setBusinessShftOriginal,
  updateBusinessApproval,
  updateCar,
  updateCarOptionSet,
  updateGasStationWithLogo,
} from "@/lib/supabase";
import type { ApprovalStatus, Car, CarOptionSet, GasStation } from "@/types/database";
import { buildBusinessListEntries } from "@/lib/business-analytics";

export const queryKeys = {
  cars: ["cars"] as const,
  car: (id: string) => ["cars", id] as const,
  carServiceHistory: (id: string) => ["cars", id, "service-history"] as const,
  carMakeLogos: ["car-make-logos"] as const,
  optionSets: ["option-sets"] as const,
  optionSet: (id: string) => ["option-sets", id] as const,
  gasStations: ["gas-stations"] as const,
  gasStation: (id: number) => ["gas-stations", id] as const,
  gasStationBrands: ["gas-station-brands"] as const,
  gasStationBrand: (id: string) => ["gas-station-brands", id] as const,
  homeNetwork: ["home-network"] as const,
  homePlatform: ["home-platform", "car-logos"] as const,
  userCount: ["user-count"] as const,
  users: ["users"] as const,
  user: (userID: string) => ["users", userID] as const,
  userProfile: (id: number) => ["users", "profile", id] as const,
  userPlatform: ["user-platform"] as const,
  fuelLogs: ["fuel-logs"] as const,
  fuelActivity: ["fuel-activity"] as const,
  userFuelActivity: (userID: string) => ["fuel-activity", "user", userID] as const,
  businesses: ["businesses"] as const,
  business: (id: number) => ["businesses", id] as const,
  connectedServices: ["connected-services"] as const,
  businessPlatform: ["business-platform"] as const,
};

export function useCars() {
  return useQuery({ queryKey: queryKeys.cars, queryFn: fetchCars });
}

export function useCar(carId: string) {
  return useQuery({
    queryKey: queryKeys.car(carId),
    queryFn: () => fetchCarById(carId),
    enabled: Boolean(carId),
  });
}

export function useCarServiceHistory(carId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.carServiceHistory(carId),
    queryFn: () => fetchServiceHistoryForCars([carId]),
    enabled: Boolean(carId) && enabled,
  });
}

export function useCarMakeLogos() {
  return useQuery({
    queryKey: queryKeys.carMakeLogos,
    queryFn: fetchCarMakeLogos,
    staleTime: 1000 * 60 * 30,
  });
}

export function useCarOptionSets() {
  return useQuery({ queryKey: queryKeys.optionSets, queryFn: fetchCarOptionSets });
}

export function useCarOptionSet(id: string) {
  return useQuery({
    queryKey: queryKeys.optionSet(id),
    queryFn: () => fetchCarOptionSetById(id),
    enabled: Boolean(id),
  });
}

export function useGasStations() {
  return useQuery({ queryKey: queryKeys.gasStations, queryFn: fetchGasStations });
}

export function useGasStation(id: number) {
  return useQuery({
    queryKey: queryKeys.gasStation(id),
    queryFn: () => fetchGasStationById(id),
    enabled: Number.isFinite(id),
  });
}

export function useGasStationBrands() {
  return useQuery({ queryKey: queryKeys.gasStationBrands, queryFn: fetchGasStationBrands });
}

export function useGasStationBrand(id: string) {
  return useQuery({
    queryKey: queryKeys.gasStationBrand(id),
    queryFn: () => fetchGasStationBrandById(id),
    enabled: Boolean(id),
  });
}

export function useCreateGasStationBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      brandName: string;
      description?: string | null;
      website?: string | null;
      phone?: number | null;
      country?: string | null;
      logoFile: File;
    }) =>
      createGasStationBrandWithLogo({
        brandName: input.brandName,
        description: input.description,
        website: input.website,
        phone: input.phone,
        country: input.country,
        logoFile: input.logoFile,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStationBrands });
    },
  });
}

export function useCreateGasStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      address: string;
      brandID: string;
      parish?: string | null;
      fuelType?: string | null;
      slug?: string | null;
      fuelPrice_87?: number | null;
      fuelPrice_90?: number | null;
      fuelPrice_Diesel?: number | null;
      fuelPrice_ULSD?: number | null;
      coordinatesLat?: number | null;
      coordinatesLong?: number | null;
      logoFile?: File | null;
    }) => createGasStationWithLogo(input),
    onSuccess: (station) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStations });
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStation(station.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.homeNetwork });
    },
  });
}

export function useUpdateGasStationBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      brandName: string;
      description?: string | null;
      website?: string | null;
      phone?: number | null;
      country?: string | null;
      logoFile?: File | null;
      brandLogoUrl?: string | null;
    }) =>
      updateGasStationBrandWithLogo(input.id, {
        brandName: input.brandName,
        description: input.description,
        website: input.website,
        phone: input.phone,
        country: input.country,
        logoFile: input.logoFile,
        brandLogoUrl: input.brandLogoUrl,
      }),
    onSuccess: (brand) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStationBrands });
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStationBrand(brand.id) });
    },
  });
}

export function useDeleteGasStationBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteGasStationBrand(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStationBrands });
      queryClient.removeQueries({ queryKey: queryKeys.gasStationBrand(id) });
    },
  });
}

export function useHomeNetworkData() {
  return useQuery({
    queryKey: queryKeys.homeNetwork,
    queryFn: async () => {
      const [cars, stations] = await Promise.all([
        fetchCarsForAnalytics(),
        fetchGasStations(),
      ]);
      return {
        cars,
        stations,
        overview: getNetworkOverview(cars, stations),
        charts: getDashboardCharts(cars, stations),
        recentCars: getRecentCars(cars),
      };
    },
  });
}

export function useHomePlatformData() {
  return useQuery({
    queryKey: queryKeys.homePlatform,
    queryFn: async () => {
      const [rows, makeLogos] = await Promise.all([
        fetchCarOptionSetSummary(),
        fetchCarMakeLogos(),
      ]);
      return { platform: getPlatformOverview(rows, makeLogos) };
    },
  });
}

export function useUserCount() {
  return useQuery({ queryKey: queryKeys.userCount, queryFn: fetchUserCount });
}

export function useUsers() {
  return useQuery({ queryKey: queryKeys.users, queryFn: fetchUsers });
}

export function useUserByUserId(userID: string) {
  return useQuery({
    queryKey: queryKeys.user(userID),
    queryFn: () => fetchUserByUserId(userID),
    enabled: Boolean(userID),
  });
}

export function useUserProfile(id: number) {
  return useQuery({
    queryKey: queryKeys.userProfile(id),
    queryFn: async () => {
      const user = await fetchUserById(id);
      if (!user?.userID) {
        return { user, cars: [], serviceHistory: [], shftServices: [] };
      }

      const cars = await fetchCarsByUserId(user.userID);
      const carIds = cars.map((car) => car.carID);
      const [serviceHistory, shftServices] = await Promise.all([
        fetchServiceHistoryForCars(carIds),
        fetchShftServicesForCars(carIds),
      ]);

      return { user, cars, serviceHistory, shftServices };
    },
    enabled: Number.isFinite(id),
  });
}

export function useUserPlatformData() {
  return useQuery({
    queryKey: queryKeys.userPlatform,
    queryFn: async () => {
      const [users, cars, serviceHistory, shftServices] = await Promise.all([
        fetchUsers(),
        fetchCars(),
        fetchServiceHistory(),
        fetchShftServices(),
      ]);

      return { users, cars, serviceHistory, shftServices };
    },
  });
}

export function useFuelActivityData() {
  return useQuery({
    queryKey: queryKeys.fuelActivity,
    queryFn: async () => {
      const [logs, users, cars, stations, brands] = await Promise.all([
        fetchFuelLogs(),
        fetchUsers(),
        fetchCars(),
        fetchGasStations(),
        fetchGasStationBrands(),
      ]);
      return { logs, users, cars, stations, brands };
    },
  });
}

export function useUserFuelActivity(userID: string) {
  return useQuery({
    queryKey: queryKeys.userFuelActivity(userID),
    queryFn: async () => {
      const [logs, users, cars, stations] = await Promise.all([
        fetchFuelLogsByUserId(userID),
        fetchUsers(),
        fetchCars(),
        fetchGasStations(),
      ]);
      const user = users.find((entry) => entry.userID === userID) ?? (await fetchUserByUserId(userID));
      return { logs, user, cars, stations };
    },
    enabled: Boolean(userID),
  });
}

export function useBusinesses() {
  return useQuery({ queryKey: queryKeys.businesses, queryFn: fetchBusinesses });
}

export function useConnectedServices() {
  return useQuery({ queryKey: queryKeys.connectedServices, queryFn: fetchConnectedServices });
}

export function useBusinessPlatformData() {
  return useQuery({
    queryKey: queryKeys.businessPlatform,
    queryFn: async () => {
      const [businesses, services, customers] = await Promise.all([
        fetchBusinesses(),
        fetchConnectedServices(),
        fetchCustomers(),
      ]);

      const entries = buildBusinessListEntries(businesses, services, customers);

      return {
        businesses,
        services,
        customers,
        entries,
      };
    },
  });
}

export function useBusinessDetail(id: number) {
  return useQuery({
    queryKey: queryKeys.business(id),
    queryFn: async () => {
      const business = await fetchBusinessById(id);
      if (!business) {
        return {
          business: null,
          connectedService: null,
          prices: [],
          customers: [],
          subscriberCars: [],
          subscriberUsers: [],
        };
      }

      const businessID = business.businessID?.trim() || "";
      const [connectedService, prices, customers] = await Promise.all([
        businessID ? fetchConnectedServiceByBusinessId(businessID) : Promise.resolve(null),
        businessID ? fetchServicePricesByBusiness(businessID) : Promise.resolve([]),
        businessID ? fetchCustomersByBusiness(businessID) : Promise.resolve([]),
      ]);

      const carIds = connectedService?.carID?.filter(Boolean) ?? [];
      const subscriberCars = await fetchCarsByCarIds(carIds);
      const userIds = [...new Set(subscriberCars.map((car) => car.userID).filter(Boolean))];
      const users = userIds.length > 0 ? await fetchUsers() : [];
      const subscriberUsers = users.filter((user) => user.userID && userIds.includes(user.userID));

      return {
        business,
        connectedService,
        prices,
        customers,
        subscriberCars,
        subscriberUsers,
      };
    },
    enabled: Number.isFinite(id),
  });
}

function invalidateBusinessQueries(queryClient: QueryClient, businessId?: number) {
  queryClient.invalidateQueries({ queryKey: queryKeys.businesses });
  queryClient.invalidateQueries({ queryKey: queryKeys.connectedServices });
  queryClient.invalidateQueries({ queryKey: queryKeys.businessPlatform });
  if (businessId != null) {
    queryClient.invalidateQueries({ queryKey: queryKeys.business(businessId) });
  }
}

export function useApproveConnectedService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewedBy }: { id: number; reviewedBy: string; businessNumericId?: number }) =>
      approveConnectedService(id, reviewedBy),
    onSuccess: (_data, variables) => {
      invalidateBusinessQueries(queryClient, variables.businessNumericId);
    },
  });
}

export function useRejectConnectedService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      reviewedBy,
      rejectionReason,
    }: {
      id: number;
      reviewedBy: string;
      rejectionReason: string;
      businessNumericId?: number;
    }) => rejectConnectedService(id, reviewedBy, rejectionReason),
    onSuccess: (_data, variables) => {
      invalidateBusinessQueries(queryClient, variables.businessNumericId);
    },
  });
}

export function useUpdateBusinessApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      approvalStatus,
      reviewedBy,
      rejectionReason,
    }: {
      id: number;
      approvalStatus: ApprovalStatus;
      reviewedBy: string;
      rejectionReason?: string | null;
    }) => updateBusinessApproval(id, { approvalStatus, reviewedBy, rejectionReason }),
    onSuccess: (_data, { id }) => {
      invalidateBusinessQueries(queryClient, id);
    },
  });
}

export function useSetBusinessShftOriginal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isShftOriginal }: { id: number; isShftOriginal: boolean }) =>
      setBusinessShftOriginal(id, isShftOriginal),
    onSuccess: (_data, { id }) => {
      invalidateBusinessQueries(queryClient, id);
    },
  });
}

function invalidateOptionSets(queryClient: QueryClient, id?: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.optionSets });
  queryClient.invalidateQueries({ queryKey: queryKeys.homePlatform });
  if (id) queryClient.invalidateQueries({ queryKey: queryKeys.optionSet(id) });
}

export function useCreateCarOptionSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Pick<CarOptionSet, "make" | "models" | "trims">) => createCarOptionSet(input),
    onSuccess: () => invalidateOptionSets(queryClient),
  });
}

export function useUpdateCarOptionSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Pick<CarOptionSet, "make" | "models" | "trims">;
    }) => updateCarOptionSet(id, updates),
    onSuccess: (_data, { id }) => invalidateOptionSets(queryClient, id),
  });
}

export function useUpdateCar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ carId, updates }: { carId: string; updates: Partial<Car> }) =>
      updateCar(carId, updates),
    onSuccess: (_data, { carId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cars });
      queryClient.invalidateQueries({ queryKey: queryKeys.car(carId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.homeNetwork });
    },
  });
}

export function useDeleteCar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (carId: string) => deleteCar(carId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cars });
      queryClient.invalidateQueries({ queryKey: queryKeys.homeNetwork });
    },
  });
}

export function useDeleteGasStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteGasStation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStations });
      queryClient.removeQueries({ queryKey: queryKeys.gasStation(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.homeNetwork });
    },
  });
}

export function useUpdateGasStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
      logoFile,
      stationLogo,
    }: {
      id: number;
      updates: Partial<GasStation>;
      logoFile?: File | null;
      stationLogo?: string | null;
    }) => updateGasStationWithLogo(id, { updates, logoFile, stationLogo }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStations });
      queryClient.invalidateQueries({ queryKey: queryKeys.gasStation(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.homeNetwork });
    },
  });
}
